import React, { useMemo, useEffect, JSX } from "react";
import { Modal } from "../../components/Modal";
import { useCardStatusStore } from "../../stores/cardStatusStore";
import { useKycStore } from "../../stores/useKycStore";
import SumSubConsentView from "./SumSubConsentView";
import KycPendingViaRain from "./KycPendingViaRain";
import PreRainTerms from "./PreRainTerms";
import { SumSubWidget } from "../../components/SumSubWidget";

interface KycModalProps {
  eoaAddress: string;
  onClose?: () => void;
  onComplete?: () => void;
}

export function KycModal({
  eoaAddress,
  onClose,
  onComplete,
}: KycModalProps): JSX.Element | null {
  const { status } = useCardStatusStore();
  const { acceptedTerms, acceptedRainTerms } = useKycStore();

  // Call onComplete when status becomes approved
  useEffect(() => {
    if (status === "approved" && onComplete) {
      onComplete();
    }
  }, [status, onComplete]);

  const getModalView = useMemo(() => {
    switch (status) {
      case "userNotExisting":
      case "initiated": {
        if (acceptedTerms) {
          return {
            title: "Complete KYC",
            component: <SumSubWidget eoaAddress={eoaAddress} />,
          };
        } else {
          return {
            title: "Verifying with Sumsub",
            component: <SumSubConsentView />,
          };
        }
      }
      case "review": {
        return {
          title: "KYC Verification is in Process",
          component: <KycPendingViaRain />,
        };
      }
      case "pending": {
        if (acceptedRainTerms) {
          return {
            title: "Accept Terms to Proceed",
            component: <KycPendingViaRain />,
          };
        } else {
          return {
            title: "KYC Verification is in Process",
            component: <PreRainTerms />,
          };
        }
      }
      case "approved": {
        // For approved status, we just return null as this SDK doesn't handle card activation
        return {
          title: "",
          component: <></>,
        };
      }
      default:
        return {
          title: "Apply for Card",
          component: <></>,
        };
    }
  }, [status, acceptedTerms, acceptedRainTerms]);

  const handleClose = onClose || (() => {});
  const { title, component } = getModalView;

  // Don't render modal if status is approved or no component
  if (status === "approved" || !component) {
    return null;
  }

  return (
    <Modal title={title} onOutsideClick={handleClose} onClose={handleClose}>
      {component}
    </Modal>
  );
}
